/**
 * <SummaryRow> — une ligne de récapitulatif : libellé à gauche, valeur à droite (molecule).
 *
 * Le corps des récapitulatifs de parcours (pause, heures en extra, frais de résiliation,
 * composeur tarifaire). `emphasis` est la ligne de total : elle grossit et passe en gras des
 * deux côtés, pour qu'on la trouve sans la chercher.
 *
 * Pas de chrome ni de filet : un récapitulatif se pose dans un `<Card>` avec un `<Divider>`
 * avant le total.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { type FontSizeKey, type FontFamilyKey } from "../../theme/tokens/typography"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"

export type SummaryRowProps = {
    label: string
    value: string
    /** Ligne de total : plus grande, en gras des deux côtés. */
    emphasis?: boolean
    /**
     * Encre des DEUX côtés — rien par défaut (libellé gris, valeur encre forte). La ligne d'une
     * remise se peint en vert : c'est ce qu'elle dit, pas un ornement.
     */
    color?: SemanticColorKey
    testID?: string
}

export function SummaryRow(props: SummaryRowProps) {
    const { label, value, emphasis = false, color, testID } = props

    const size: FontSizeKey = emphasis ? "text-lg" : "text-md"
    const labelWeight: FontFamilyKey = emphasis ? "bold" : "regular"
    const labelColor: SemanticColorKey = color ?? (emphasis ? "text-strong" : "text-muted")
    const valueColor: SemanticColorKey = color ?? "text-strong"

    return (
        <Row gap="4" justify="between" align="center" testID={testID}>
            <Text size={size} weight={labelWeight} color={labelColor} style={styles.label}>
                {label}
            </Text>
            <Text size={size} weight="bold" color={valueColor} style={styles.value}>
                {value}
            </Text>
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    label: { flexShrink: 1 },
    /** La valeur ne se réduit pas : un montant tronqué ne veut plus rien dire. */
    value: { flexShrink: 0 },
}))
